import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { monthOptions } from "@/components/PeriodPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Facebook, Linkedin, Globe, HelpCircle, TrendingUp, TrendingDown, AlertTriangle, CalendarDays } from "lucide-react";

const CHANNEL_META = {
  Facebook: { icon: Facebook, color: "text-blue-700", bg: "bg-blue-50" },
  LinkedIn: { icon: Linkedin, color: "text-sky-700", bg: "bg-sky-50" },
  "Sito web": { icon: Globe, color: "text-emerald-700", bg: "bg-emerald-50" },
  Altro: { icon: HelpCircle, color: "text-slate-700", bg: "bg-slate-100" },
};

const ALERT_META = {
  up: { label: "In crescita", cls: "bg-emerald-100 text-emerald-700", icon: TrendingUp },
  down: { label: "In calo", cls: "bg-amber-100 text-amber-700", icon: TrendingDown },
  drop: { label: "Azzerato", cls: "bg-rose-100 text-rose-700", icon: TrendingDown },
  new: { label: "Nuovo", cls: "bg-violet-100 text-violet-700", icon: TrendingUp },
};

const WINDOWS = [
  { days: 7, label: "Ultimi 7 giorni" },
  { days: 15, label: "Ultimi 15 giorni" },
  { days: 30, label: "Ultimi 30 giorni" },
];

const MONTHS = monthOptions(12);

export default function SourcesAnalytics() {
  const [mode, setMode] = useState("days"); // "days" | "month"
  const [days, setDays] = useState(7);
  const [month, setMonth] = useState(MONTHS[0]);

  const sourcesParams = mode === "month"
    ? { date_from: month.date_from, date_to: month.date_to }
    : { days };

  const { data: sources, isLoading: ls } = useQuery({
    queryKey: ["sources", mode, mode === "month" ? month.key : days],
    queryFn: async () => (await api.get("/analytics/sources", { params: sourcesParams })).data,
  });
  const { data: periods, isLoading: lp } = useQuery({
    queryKey: ["periods", days],
    queryFn: async () => (await api.get("/analytics/periods", { params: { days } })).data,
    enabled: mode === "days",
  });

  const channelNames = sources?.channel_names || [];
  const periodLabel = mode === "month" ? month.label : `ultimi ${days} giorni`;

  return (
    <div className="space-y-8" data-testid="sources-page">
      {/* Mode + period selector */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="mode-picker">
          <span className="mr-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <CalendarDays className="h-4 w-4" /> Vista
          </span>
          {[{ id: "days", label: "Giorni" }, { id: "month", label: "Mese" }].map((m) => (
            <button
              key={m.id}
              data-testid={`mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                mode === m.id
                  ? "bg-slate-900 text-white shadow-sm"
                  : "border border-border bg-secondary/40 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === "days" ? (
          <div className="flex flex-wrap gap-2" data-testid="window-picker">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                data-testid={`window-${w.days}`}
                onClick={() => setDays(w.days)}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  days === w.days
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-border bg-white text-muted-foreground hover:bg-secondary"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="month-picker">
            {MONTHS.map((mo) => (
              <button
                key={mo.key}
                data-testid={`month-${mo.key}`}
                onClick={() => setMonth(mo)}
                className={`flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  month.key === mo.key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "border border-border bg-white text-muted-foreground hover:bg-secondary"
                }`}
              >
                {mo.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Total for the period */}
      {!ls && (
        <p className="text-sm text-muted-foreground" data-testid="period-total">
          <span className="font-display text-lg font-bold text-foreground">{sources?.total ?? 0}</span> richieste ricevute — {periodLabel}
        </p>
      )}

      {/* Channel ranking */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {(ls ? Array.from({ length: 4 }) : (sources?.channels || [])).map((c, i) => {
          if (ls) return <Skeleton key={i} className="h-28 w-full" />;
          const meta = CHANNEL_META[c.channel] || CHANNEL_META.Altro;
          const Icon = meta.icon;
          return (
            <Card key={c.channel} className="shadow-sm" data-testid={`channel-${c.channel}`}>
              <CardContent className="p-5">
                <div className={`mb-3 inline-flex h-9 w-9 items-center justify-center rounded-md ${meta.bg}`}>
                  <Icon className={`h-5 w-5 ${meta.color}`} />
                </div>
                <p className="font-display text-3xl font-bold tracking-tight">{c.count}</p>
                <p className="mt-1 text-sm font-medium">{c.channel}</p>
                <p className="text-xs text-muted-foreground">{c.pct}% del totale</p>
              </CardContent>
            </Card>
          );
        })}
        {!ls && (sources?.channels || []).length === 0 && (
          <p className="col-span-full py-8 text-center text-sm text-muted-foreground">Nessuna richiesta nel periodo selezionato.</p>
        )}
      </div>

      {/* Alerts (only in rolling-days mode) */}
      {mode === "days" && !lp && (periods?.alerts?.length || 0) > 0 && (
        <Card className="border-amber-200 bg-amber-50 shadow-sm" data-testid="change-alerts">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center gap-2 font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" /> Variazioni rilevanti — ultimi {days} giorni vs {days} precedenti
            </div>
            <AlertList rows={periods?.alerts} />
          </CardContent>
        </Card>
      )}

      {/* Course ranking */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="font-display text-base tracking-tight">
            Classifica corsi — {periodLabel}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mode === "month" ? (
            ls ? <Skeleton className="h-72 w-full" /> : <MonthTable rows={sources?.per_course} />
          ) : (
            lp ? <Skeleton className="h-72 w-full" /> : (
              <CompareTable rows={periods?.ranking} labelCur={`Ultimi ${days}gg`} labelPrev={`${days}gg precedenti`} />
            )
          )}
        </CardContent>
      </Card>

      {/* Per course per channel */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="font-display text-base tracking-tight">Richieste per corso e per canale — {periodLabel}</CardTitle>
        </CardHeader>
        <CardContent>
          {ls ? <Skeleton className="h-72 w-full" /> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Corso</TableHead>
                    {channelNames.map((c) => (
                      <TableHead key={c} className="text-right text-xs font-semibold uppercase tracking-wider">{c}</TableHead>
                    ))}
                    <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">Totale</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(sources?.per_course || []).length === 0 ? (
                    <TableRow><TableCell colSpan={channelNames.length + 2} className="py-8 text-center text-sm text-muted-foreground">Nessun dato per questo periodo.</TableCell></TableRow>
                  ) : (
                    (sources?.per_course || []).map((row) => (
                      <TableRow key={row.course} data-testid="course-channel-row">
                        <TableCell className="text-sm font-medium">{row.course}</TableCell>
                        {channelNames.map((c) => (
                          <TableCell key={c} className="text-right text-sm text-muted-foreground">{row.channels[c] || 0}</TableCell>
                        ))}
                        <TableCell className="text-right text-sm font-semibold">{row.total}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MonthTable({ rows }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-secondary/60">
          <TableHead className="text-xs font-semibold uppercase tracking-wider">#</TableHead>
          <TableHead className="text-xs font-semibold uppercase tracking-wider">Corso</TableHead>
          <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">Richieste</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={r.course} data-testid="month-course-row">
            <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
            <TableCell className="text-sm font-medium">{r.course}</TableCell>
            <TableCell className="text-right text-sm font-semibold">{r.total}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AlertList({ rows }) {
  return (
    <div className="grid grid-cols-1 gap-1.5 lg:grid-cols-2">
      {(rows || []).length === 0 && <p className="text-xs text-amber-700">Nessuna variazione rilevante.</p>}
      {(rows || []).map((r) => {
        const m = ALERT_META[r.alert] || ALERT_META.up;
        const Icon = m.icon;
        return (
          <div key={r.course} className="flex items-center justify-between rounded-md bg-white px-3 py-1.5 text-sm ring-1 ring-amber-200">
            <span className="font-medium">{r.course}</span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{r.previous} → {r.current}</span>
              <Badge className={`gap-1 rounded-full font-semibold hover:bg-inherit ${m.cls}`}>
                <Icon className="h-3 w-3" />
                {r.delta_pct !== null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : m.label}
              </Badge>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CompareTable({ rows, labelCur, labelPrev }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-secondary/60">
          <TableHead className="text-xs font-semibold uppercase tracking-wider">#</TableHead>
          <TableHead className="text-xs font-semibold uppercase tracking-wider">Corso</TableHead>
          <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">{labelCur}</TableHead>
          <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">{labelPrev}</TableHead>
          <TableHead className="text-right text-xs font-semibold uppercase tracking-wider">Variazione</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => {
          const m = ALERT_META[r.alert];
          return (
            <TableRow key={r.course} data-testid="period-course-row">
              <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
              <TableCell className="text-sm font-medium">{r.course}</TableCell>
              <TableCell className="text-right text-sm">{r.current}</TableCell>
              <TableCell className="text-right text-sm text-muted-foreground">{r.previous}</TableCell>
              <TableCell className="text-right text-sm">
                {m ? (
                  <Badge className={`gap-1 rounded-full font-semibold hover:bg-inherit ${m.cls}`}>
                    {r.delta_pct !== null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : m.label}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">{r.delta > 0 ? "+" : ""}{r.delta}</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function Empty() {
  return <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Nessun dato per questo periodo.</div>;
}
